import {
	Icon,
	IconArrowRightArcDots,
	IconCheckmarkStrong,
	IconCrossed,
	IconDisinfected,
	IconGasPump,
	IconTime,
	IconWrench,
} from '@flixbus/honeycomb-icons-react';

import { ACTIVITY_RENDER_TYPES } from 'common/ActivityChip/const';

// TODO: Consolidate extra activity type, label, and icon metadata with ACTIVITY_RENDER_TYPES
// into a single source of truth and add complete coverage for the activity contract.
export const EXTRA_ACTIVITY_TYPES = {
	signOn: ACTIVITY_RENDER_TYPES.signOn,
	signOff: ACTIVITY_RENDER_TYPES.signOff,
	fuelling: ACTIVITY_RENDER_TYPES.fuelling,
	cleaning: ACTIVITY_RENDER_TYPES.cleaning,
	garaging: ACTIVITY_RENDER_TYPES.garaging,
	lapseTime: ACTIVITY_RENDER_TYPES.lapseTime,
};

export const EXTRA_ACTIVITY_LABELS = {
	[EXTRA_ACTIVITY_TYPES.signOn]: 'Sign on',
	[EXTRA_ACTIVITY_TYPES.signOff]: 'Sign off',
	[EXTRA_ACTIVITY_TYPES.fuelling]: 'Fuelling',
	[EXTRA_ACTIVITY_TYPES.cleaning]: 'Cleaning',
	[EXTRA_ACTIVITY_TYPES.garaging]: 'Garaging',
	[EXTRA_ACTIVITY_TYPES.lapseTime]: 'Lapse time',
};

const mapTypesToOptions = (types) =>
	types.map((type) => ({
		type,
		label: EXTRA_ACTIVITY_LABELS[type],
	}));

export const CREATABLE_EXTRA_ACTIVITY_OPTIONS = [
	EXTRA_ACTIVITY_TYPES.garaging,
	EXTRA_ACTIVITY_TYPES.signOn,
	EXTRA_ACTIVITY_TYPES.signOff,
	EXTRA_ACTIVITY_TYPES.cleaning,
	EXTRA_ACTIVITY_TYPES.fuelling,
	EXTRA_ACTIVITY_TYPES.lapseTime,
].map((type) => ({
	type,
	label: EXTRA_ACTIVITY_LABELS[type],
}));

export const PRE_DRIVING_EXTRA_ACTIVITY_OPTIONS = mapTypesToOptions([
	EXTRA_ACTIVITY_TYPES.signOn,
	EXTRA_ACTIVITY_TYPES.garaging,
	EXTRA_ACTIVITY_TYPES.fuelling,
]);

export const POST_DRIVING_EXTRA_ACTIVITY_OPTIONS = mapTypesToOptions([
	EXTRA_ACTIVITY_TYPES.lapseTime,
	EXTRA_ACTIVITY_TYPES.fuelling,
	EXTRA_ACTIVITY_TYPES.cleaning,
	EXTRA_ACTIVITY_TYPES.garaging,
	EXTRA_ACTIVITY_TYPES.signOff,
]);

const mergeOptions = (...optionGroups) => {
	const seenTypes = new Set();

	return optionGroups.flat().filter(({ type }) => {
		if (seenTypes.has(type)) return false;
		seenTypes.add(type);
		return true;
	});
};

export const getExtraActivityLocationOptions = (locations = []) => {
	if (!Array.isArray(locations) || locations.length === 0) return [];

	const firstLocation = locations[0];
	const lastLocation = locations[locations.length - 1];

	if (!lastLocation || firstLocation.id === lastLocation.id) return [firstLocation].filter(Boolean);

	return [firstLocation, lastLocation].filter(Boolean);
};

export const getExtraActivityOptionsForStation = (stationId, locations = []) => {
	const locationOptions = getExtraActivityLocationOptions(locations);
	const firstLocation = locationOptions[0];
	const lastLocation = locationOptions[locationOptions.length - 1];

	if (!stationId || !firstLocation) return [];

	const isFirstLocation = stationId === firstLocation.id;
	const isLastLocation = stationId === lastLocation?.id;

	if (isFirstLocation && isLastLocation) {
		return mergeOptions(PRE_DRIVING_EXTRA_ACTIVITY_OPTIONS, POST_DRIVING_EXTRA_ACTIVITY_OPTIONS);
	}

	if (isFirstLocation) return PRE_DRIVING_EXTRA_ACTIVITY_OPTIONS;
	if (isLastLocation) return POST_DRIVING_EXTRA_ACTIVITY_OPTIONS;

	return [];
};

export const isExtraActivityType = (type) => Object.values(EXTRA_ACTIVITY_TYPES).includes(type);

export const getExtraActivityLabel = (type) => EXTRA_ACTIVITY_LABELS[type] ?? type;

const EXTRA_ACTIVITY_ICONS = {
	[EXTRA_ACTIVITY_TYPES.signOn]: IconCheckmarkStrong,
	[EXTRA_ACTIVITY_TYPES.signOff]: IconCrossed,
	[EXTRA_ACTIVITY_TYPES.garaging]: IconArrowRightArcDots,
	[EXTRA_ACTIVITY_TYPES.fuelling]: IconGasPump,
	[EXTRA_ACTIVITY_TYPES.lapseTime]: IconTime,
	[EXTRA_ACTIVITY_TYPES.cleaning]: IconDisinfected,
};

export const getExtraActivityIcon = (type) => {
	const InlineIcon = EXTRA_ACTIVITY_ICONS[type] ?? IconWrench;

	return <Icon InlineIcon={InlineIcon} />;
};
